import type {AnswerType} from 'components/duolessons/lessontypes/conceptcheck/answerModes';
import type {
  PostToyOption,
  PostToyProps,
} from 'components/richTextEditor/TipTap/extensions/easterEggToys';
import type {ColorSchemePreference} from 'contexts/ColorSchemeContext';
import type {RoadmapStyle} from 'contexts/RoadmapStyleContext';
import type {
  ForumStyle,
  SidebarLayout,
  SidebarStyle,
} from 'contexts/UiPreferencesContext';
import type {CourseThemeDocument} from 'course/models/courseThemeDocument';
import type {PuzzleCoachProps} from 'course/puzzles/stage/PuzzleCoach';
import {createContext, useContext, type ComponentType} from 'react';

/**
 * Optional component overrides an external deploy wrapper can provide to
 * customise CIP's application shell without patching its source files.
 *
 * Each key is optional. When a key is present its value replaces the
 * corresponding CIP default; when the key is absent the default is used.
 *
 * @example
 * ```tsx
 * // PAI wrapper — supply a custom landing page, keep everything else.
 * <AppOverridesContext.Provider value={{ SplashPage: PaiSplashPage }}>
 *   <Router />
 * </AppOverridesContext.Provider>
 * ```
 */
export interface AppOverrides {
  /**
   * Override for the landing/splash page rendered at the index route (`/`).
   * When omitted, the default CIP {@link Landing} component is used.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  SplashPage?: ComponentType<any>;
  /**
   * Override for the full-screen loading overlay shown while the app
   * initialises. When omitted, the default CIP {@link LoadingComponent} is used.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  LoadingPage?: ComponentType<any>;
  /**
   * Override for the error page shown when an unhandled error occurs. Receives
   * the same props as the default CIP {@link ErrorPage}. When omitted, the
   * default CIP {@link ErrorPage} is used.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  ErrorPage?: ComponentType<any>;
  /**
   * The application name used as the browser tab title at the root route (`/`).
   * When omitted, defaults to `"Code in Place"`.
   */
  appName?: string;
  /**
   * A second field on the `global/webapp` document, next to the shared
   * `version`, whose change prompts this app's open tabs to reload. Lets a
   * deploy wrapper nudge only its own users after a deploy. When omitted,
   * only `version` is watched.
   */
  versionField?: string;
  /**
   * Override providing this deploy wrapper's own nested routes, mounted at
   * `/:courseId/tools/*` (nested under the course route so switching between
   * a tool and the rest of the course doesn't unmount/remount CourseProvider
   * and friends). Must render its own `<Routes>`/`<Route>` tree (see
   * `react-router-dom`) — CIP mounts it as-is with no props. Intended for
   * deploy wrappers (e.g. PAI) that ship a growing collection of standalone
   * interactive tools without ever needing a CIP source change per tool. When
   * omitted, `/:courseId/tools/*` renders a 404 — CIP itself ships no tools.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  ToolRoutes?: ComponentType<any>;
  /**
   * Eager replacement for the Math IDE assignment route (`mide/a/:assignmentId`).
   * The Math IDE is a heavy subsystem, so CIP's default loads it with
   * `React.lazy` — the course chunk never downloads it for students who never
   * open it. A deploy wrapper MAY supply an eagerly-imported component here to
   * get it into its initial bundle, but weigh the cost: the eager import sits
   * in front of every page, including the landing page (this is why PAI
   * stopped using it — see
   * [#3616](https://github.com/CodeinPlace/cip/issues/3616)). When omitted,
   * the lazy default is used.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  MathIdeRoute?: ComponentType<any>;
  /**
   * Eager replacement for the Math assignment editor route
   * (`mathassignments/:assignmentId`). Lazy by default (see {@link MathIdeRoute}
   * for the rationale). When omitted, the lazy default is used.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  MathAssignmentRoute?: ComponentType<any>;
  /**
   * Eager replacement for the slide editor/presentation routes (`slides/*`).
   * The slide editor is a heavy subsystem, so CIP's default loads it with
   * `React.lazy`. See {@link MathIdeRoute} for the bundle-size trade-off of
   * supplying an eager component. When omitted, the lazy default is used.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  SlidesRoutes?: ComponentType<any>;
  /**
   * A course theme supplied by the deploy wrapper, applied to every course
   * rendered in this deploy. Provides the deploy's look and feel (colors, fonts,
   * brand) synchronously — no Firestore round-trip — while remaining fully
   * generic: it is a partial {@link CourseThemeDocument}, identical in shape to
   * the per-course Firestore theme doc. {@link CourseThemeProvider} folds this in
   * beneath any per-course Firestore theme, so a specific course can still
   * override individual fields. When omitted, only the Firestore theme (or the
   * hardcoded defaults) applies. See {@link AppOverrides} usage in the deploy's
   * entrypoint (e.g. PAI's `main.tsx`).
   */
  courseTheme?: Partial<CourseThemeDocument>;
  /**
   * A dark counterpart to {@link courseTheme}, overlaid on top of it when the
   * user's resolved color scheme is dark (see `ColorSchemeProvider`). Supplying
   * it is what makes the appearance setting exist at all: when omitted, the
   * deploy has no dark scheme, the settings page hides the control, and every
   * user gets {@link courseTheme} regardless of any stored preference.
   */
  courseThemeDark?: Partial<CourseThemeDocument>;
  /**
   * The color scheme for users who have not stored a preference. Only
   * meaningful when {@link courseThemeDark} is supplied. When omitted,
   * defaults to `'light'`.
   */
  defaultColorScheme?: ColorSchemePreference;
  /**
   * Whether the course settings page offers a choice of course sidebar style
   * (see `UiPreferencesProvider` and the variants in
   * `course/components/sidebar/menuItemStyles`). When omitted, the control is
   * hidden and every user gets {@link defaultSidebarStyle}.
   */
  hasSidebarStyleSetting?: boolean;
  /**
   * The sidebar style for every user when {@link hasSidebarStyleSetting} is
   * off, and for users who have not stored a choice when it is on. When
   * omitted, defaults to `'classic'`.
   */
  defaultSidebarStyle?: SidebarStyle;
  /**
   * Whether the course settings page offers a choice of course sidebar
   * layout: collapsible submenus or grouped direct links (see
   * `UiPreferencesProvider` and `course/components/sidebar/GroupedSidebarMenu`).
   * When omitted, the control is hidden and every user gets
   * {@link defaultSidebarLayout}.
   */
  hasSidebarLayoutSetting?: boolean;
  /**
   * The sidebar layout for every user when {@link hasSidebarLayoutSetting} is
   * off, and for users who have not stored a choice when it is on. When
   * omitted, defaults to `'submenus'`.
   */
  defaultSidebarLayout?: SidebarLayout;
  /**
   * Whether the course settings page offers a choice of main-forum layout:
   * the classic two-pane forum or the feed experiment (see
   * `UiPreferencesProvider` and `course/forum/pages/feed`). When omitted, the
   * control is hidden and every user gets {@link defaultForumStyle}.
   */
  hasForumStyleSetting?: boolean;
  /**
   * The forum layout for every user when {@link hasForumStyleSetting} is
   * off, and for users who have not stored a choice when it is on. When
   * omitted, defaults to `'classic'`.
   */
  defaultForumStyle?: ForumStyle;
  /**
   * Alternative roadmaps for the student home, offered alongside the classic
   * module-card list (see `RoadmapStyleProvider` and
   * `RoadmapViewSwitcher`). Each view receives no props and reads the roadmap
   * and completion contexts itself. When omitted or empty, the classic list
   * is always used and the layout controls are hidden.
   */
  roadmapViews?: readonly RoadmapViewOption[];
  /**
   * Whether the layout choice is offered at all, on the settings page and as
   * a switcher above the roadmap. Only meaningful when {@link roadmapViews}
   * is non-empty. When omitted, the controls are hidden and every user gets
   * {@link defaultRoadmapStyle}.
   */
  hasRoadmapStyleSetting?: boolean;
  /**
   * The roadmap layout for every user when {@link hasRoadmapStyleSetting} is
   * off, and for users who have not stored a choice when it is on:
   * `'classic'` or the id of one of {@link roadmapViews}. When omitted, or
   * when it names no supplied view, defaults to `'classic'`.
   */
  defaultRoadmapStyle?: RoadmapStyle;
  /**
   * A deploy-provided editor for the alternative roadmaps' look and feel,
   * rendered as a page of the roadmap editor at
   * `/:courseId/roadmapeditor/look` and linked from its header. Receives no
   * props and runs inside the course page's providers. May be a `lazy`
   * component; the editor wraps it in a suspense boundary. When omitted, the
   * page and the link are absent.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  RoadmapLookAndFeelEditor?: ComponentType<any>;
  /**
   * The character beside the coach's speech bubble on the puzzle pages
   * (course feature `puzzles`), drawn in the given mood. May be a `lazy`
   * component; the puzzle stage wraps it in a suspense boundary. When
   * omitted, a puzzle-piece badge stands in.
   */
  PuzzleCoach?: ComponentType<PuzzleCoachProps>;
  /**
   * Override for the course-home fallback used by the exit/back button
   * ({@link HomeExitButton}) when no flow-specific destination applies and
   * there's no navigation history to return to. When omitted, defaults to
   * `` `/${courseId}` ``.
   */
  defaultHomeUrl?: string;
  /**
   * Override for the course-home route (`/:courseId` and `/:courseId/home`).
   * Receives no props. When omitted, the default CIP {@link HomeRedirect} is
   * used, which redirects to a role-specific home (e.g. `studenthome`).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  CourseHomePage?: ComponentType<any>;
  /**
   * Override for the teacher home route (`/:courseId/teachinghome`). Receives
   * no props and renders inside the course page, so the course navbar and
   * sidebar stay around it. It may render the default `NewTeachingHome`
   * itself for phases it does not handle. May be a `lazy` component; the
   * route wraps it in a suspense boundary. It places staff's View as panel
   * (`StaffViewAsPanel` for teachers) in its own layout. When omitted,
   * `NewTeachingHome` is used.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  TeachingHomePage?: ComponentType<any>;
  /**
   * Override for the team page route (`/team`). Receives no props. When omitted,
   * the default CIP {@link Team} page is used. A deploy wrapper (e.g. PAI)
   * supplies its own team page here so the shared footer's "Team" link resolves
   * to a deploy-specific team rather than the CIP team.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  TeamPage?: ComponentType<any>;
  /**
   * The course Toolbox page (`/:courseId/toolbox`, sidebar feature
   * `toolbox`): a reference of the definitions and rules from lecture.
   * Receives no props and renders inside the course page, so it has the
   * course contexts. May be lazy; the route supplies the suspense boundary.
   * When omitted, the route renders nothing, since the content belongs to the
   * deploy. When set, the learn page (`/:courseId/learn`) also shows a Toolbox
   * card above the lesson list, whether or not the sidebar item is on.
   */
  ToolboxPage?: ComponentType<Record<string, never>>;
  /**
   * The class research hub (`/:courseId/research/*`, sidebar feature
   * `research`): groups, live shared documents, discussion, results and data
   * for a class research project. Receives no props and renders inside the
   * course page, so it has the course contexts; it routes its own sub-paths.
   * May be lazy; the route supplies the suspense boundary. When omitted, the
   * route renders nothing, since the project belongs to the deploy.
   */
  ResearchHubPage?: ComponentType<Record<string, never>>;
  /**
   * The course T-shirt design contest page (`/:courseId/tshirt`). Receives no
   * props and renders inside the course page, so it has the course contexts.
   * May be lazy; the route supplies the suspense boundary. When omitted, the
   * route renders nothing, since the contest belongs to the deploy.
   */
  TshirtContestPage?: ComponentType<Record<string, never>>;
  /**
   * Extra content for the in-lesson outline, shown after the lesson's steps
   * (desktop docked outline and mobile drawer). Receives the open lesson's
   * id; renders nothing for lessons it has no content for. May be lazy; the
   * outline supplies a suspense boundary with no fallback. When omitted, the
   * outline shows only steps, readings, and examples.
   */
  LessonOutlineExtra?: ComponentType<{lessonId: string}>;
  /**
   * Extra content under a lesson concept question's answer area, such as a
   * link to a calculator. Receives the open lesson's id and the question's
   * answer mode; renders nothing where it has no content. May be lazy; the
   * question supplies a suspense boundary with no fallback. When omitted, the
   * question shows only its own answer area.
   */
  ConceptQuestionExtra?: ComponentType<{
    lessonId: string;
    answerType: AnswerType;
  }>;
  /**
   * Hides the "Readers" column (the Karel and Python reader links) on the
   * learn page (`/:courseId/learn`). Meant for deploys whose courses have no
   * readers to link to. When omitted, the readers column is shown.
   */
  hidesLearnReaders?: boolean;
  /**
   * Sends the weekly section guide (`/:courseId/section/:sectionWeek/guide`)
   * to the teacher handbook's chapter for that week
   * (`/:courseId/teacherhandbook/week{N}`) instead of rendering the guide.
   * Meant for deploys whose handbook is the section leaders' lesson plan.
   * When omitted, the guide page renders.
   */
  sendsSectionGuideToHandbook?: boolean;
  /**
   * Hides the admin-editable intro text at the top of the learn page
   * (`/:courseId/learn`), stored at `course/{courseId}/info/learningCenter`.
   * When omitted, the intro is shown: admins see an editor and students see
   * the rendered text.
   */
  hidesLearnIntro?: boolean;
  /**
   * A course book shown as a card above the lesson list on the learn page
   * (`/:courseId/learn`), opened in a new tab. When omitted, no book card is
   * shown.
   */
  learnBookLink?: {
    /** Link text. */
    label: string;
    /** Absolute URL of the book. */
    href: string;
  };
  /**
   * Wraps event names in full on the student home calendar, growing each
   * slot row to fit (see `CalendarProps.wrapsEventNames`). When omitted, bars
   * stay one line tall and long names are ellipsized.
   */
  wrapsStudentCalendarEventNames?: boolean;
  /**
   * Lets the student home's main column grow into spare width on a wide
   * screen, up to a limit, so its calendar and roadmap get wider (see
   * `CoursePageBodyContainer`'s `isMainColumnFlexible`). When omitted, the
   * column keeps its fixed width.
   */
  widensStudentHome?: boolean;
  /**
   * Replaces the world map and the line under it in the student home's
   * course card (the "Instructors" box). Receives no props. May be a `lazy`
   * component; the card wraps it in a suspense boundary. When omitted, the
   * map of where Code in Place students are from and its caption are shown.
   */
  CourseCardMap?: ComponentType;
  /**
   * The toys staff may put in forum posts and replies, as the editor's insert
   * menu lists them. A post's toy node shows only an id listed here, drawn by
   * {@link PostToy}. When omitted, the insert button is hidden and toy nodes
   * show nothing.
   */
  postToyOptions?: readonly PostToyOption[];
  /**
   * Draws one of {@link postToyOptions} inside a post. Supply it lazily: it
   * loads only for posts that hold a toy. When omitted, toy nodes show
   * nothing.
   */
  PostToy?: ComponentType<PostToyProps>;
  /**
   * Per-course overrides, keyed by courseId. When the active course matches a
   * key, the slots in {@link CourseAppOverrides} take precedence over the
   * deploy-wide slots above; any slot absent for that course falls back to the
   * deploy-wide value, then the CIP default. Only presentational slots are
   * course-scoped (see {@link CourseAppOverrides}); routing/eager slots stay
   * deploy-wide because they are bundle-structure decisions, not per-course UI.
   * How the active course is resolved from a URL is a consumer concern — the
   * loading overlay uses `resolveLoadingCourseId` (see
   * `components/loading/Loading.tsx`). When omitted, every course uses the
   * deploy-wide slots.
   */
  courseOverrides?: Record<string, CourseAppOverrides>;
}

/** One alternative student-home roadmap a deploy offers. */
export interface RoadmapViewOption {
  /** Stable id stored as the user's choice. Must not be `'classic'`. */
  id: string;
  /** Short label shown in the settings select and the in-page switcher. */
  label: string;
  /** The view; receives no props. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  component: ComponentType<any>;
}

/**
 * The subset of {@link AppOverrides} slots that can be overridden per-course
 * via {@link AppOverrides.courseOverrides}. Presentational only.
 */
export interface CourseAppOverrides {
  /**
   * Override for this course's standard (non-IDE) loading view. Unlike the
   * deploy-wide {@link AppOverrides.LoadingPage} — which replaces the entire
   * loading overlay including the IDE skeleton — this per-course override
   * replaces only the standard loading view (see `LoadingComponent` in
   * `components/loading/Loading.tsx`); IDE loads still show the IDE skeleton.
   * When omitted, the course uses the deploy-wide loader.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- intentionally permissive: callers supply their own props
  LoadingPage?: ComponentType<any>;
}

/**
 * Context that holds the active {@link AppOverrides}. Provide a value at the
 * root of the tree (see {@link App}) to override specific CIP components.
 * Defaults to an empty object so every consumer is safe to call without a
 * provider.
 */
export const AppOverridesContext = createContext<AppOverrides>({});

/**
 * Returns the active {@link AppOverrides} from context.
 *
 * Deploy-wide slots are returned as-is. Per-course slots live under
 * {@link AppOverrides.courseOverrides}; consumers that vary by course resolve
 * the active course themselves (the loading overlay uses `resolveLoadingCourseId`
 * — see `components/loading/Loading.tsx`) and index into `courseOverrides`.
 *
 * @returns The nearest {@link AppOverridesContext} value. Defaults to `{}`
 *   (no overrides) when no provider is present.
 */
export function useAppOverrides(): AppOverrides {
  return useContext(AppOverridesContext);
}
